/**
 * Command-bar icons (docs/build/Fyr_command_bar_UX_spec.md §3.1): 20×20 line icons in the text colour, so they follow
 * the light / dark mode and every skin. Commands that have always shown a glyph beside their label (⤒ Upload, + New …,
 * ☁ Connect a folder, 💬 Discuss, 📞 Start call…, 🗑 Recycle bin, ✨ AI) keep it — `Glyph` — so what people read on
 * the button is unchanged; the rest use these.
 */
import type { ReactNode } from 'react';

const Line = ({ children }: { children: ReactNode }) => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    focusable="false"
  >
    {children}
  </svg>
);

/** A glyph the command has always carried (kept so its visible text doesn't change), sized like an icon. */
export const Glyph = ({ g }: { g: string }) => (
  <span className="cmd-glyph" aria-hidden>
    {g}
  </span>
);

export const SaveIcon = () => (
  <Line>
    <path d="M4 3.5h9.5L16.5 6.5V16.5H4z" />
    <path d="M7 3.5v4h6v-4M7 16.5v-5h6v5" />
  </Line>
);
export const DownloadIcon = () => (
  <Line>
    <path d="M10 3.5v9M6 9l4 4 4-4M4 16.5h12" />
  </Line>
);
export const UploadVersionIcon = () => (
  <Line>
    <path d="M10 13V4M6 7.5 10 3.5l4 4M4 16.5h12" />
  </Line>
);
export const AccessIcon = () => (
  <Line>
    <rect x="4.5" y="9" width="11" height="7.5" rx="1.2" />
    <path d="M7 9V6.5a3 3 0 0 1 6 0V9" />
  </Line>
);
export const ShareIcon = () => (
  <Line>
    <circle cx="14.5" cy="5" r="2" />
    <circle cx="5.5" cy="10" r="2" />
    <circle cx="14.5" cy="15" r="2" />
    <path d="M7.3 9l5.4-3M7.3 11l5.4 3" />
  </Line>
);
export const PublishIcon = () => (
  <Line>
    <path d="M8 4.5H4.5v11h11V12" />
    <path d="M11 3.5h5.5V9M16.5 3.5 9 11" />
  </Line>
);
export const DeleteIcon = () => (
  <Line>
    <path d="M3.5 5.5h13M8 5.5V3.5h4v2M5.5 5.5l.8 11h7.4l.8-11M8.5 8.5v5M11.5 8.5v5" />
  </Line>
);
export const ExportIcon = () => (
  <Line>
    <path d="M4 12.5v4h12v-4M10 3.5v9M6.5 7 10 3.5 13.5 7" />
  </Line>
);
export const BrandIcon = () => (
  <Line>
    <path d="M10 3.5a6.5 6.5 0 1 0 0 13c1 0 1.5-.6 1.5-1.4 0-1.3-1.2-1.6-1.2-2.6 0-.8.7-1.3 1.5-1.3h1.7a3 3 0 0 0 3-3c0-2.6-2.9-4.7-6.5-4.7z" />
    <circle cx="6.8" cy="9" r=".9" />
    <circle cx="9.5" cy="6.3" r=".9" />
    <circle cx="13" cy="7.2" r=".9" />
  </Line>
);
export const ArchiveIcon = () => (
  <Line>
    <rect x="3.5" y="4" width="13" height="3.5" rx=".8" />
    <path d="M4.5 7.5v8.5h11V7.5M8.5 10.5h3" />
  </Line>
);
export const RestoreIcon = () => (
  <Line>
    <path d="M4 10a6 6 0 1 0 1.8-4.3M4 3.5v3h3" />
  </Line>
);
export const ClockIcon = () => (
  <Line>
    <circle cx="10" cy="10" r="6.5" />
    <path d="M10 6.5V10l2.5 1.5" />
  </Line>
);
export const MoreIcon = () => (
  <Line>
    <circle cx="4.5" cy="10" r=".6" fill="currentColor" />
    <circle cx="10" cy="10" r=".6" fill="currentColor" />
    <circle cx="15.5" cy="10" r=".6" fill="currentColor" />
  </Line>
);
export const CaretIcon = () => (
  <svg width={12} height={12} viewBox="0 0 12 12" aria-hidden focusable="false">
    <path
      d="M2.5 4.5 6 8l3.5-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
    />
  </svg>
);
export const SearchIcon = () => (
  <Line>
    <circle cx="8.5" cy="8.5" r="5" />
    <path d="m12.5 12.5 4 4" />
  </Line>
);
export const GoIcon = () => (
  <Line>
    <path d="M4 10h11M11 6l4 4-4 4" />
  </Line>
);
export const LabelsIcon = () => (
  <Line>
    <rect x="3.5" y="5.5" width="13" height="9" rx="1.5" />
    <path d="M6.5 10h7" />
  </Line>
);
